/**
 * Sign-in.
 *
 * THE PRIMARY PATH IS MICROSOFT 365 SSO. This is not a preference — MFA for this
 * deployment is delegated to M365 Conditional Access, so there is no app-level MFA
 * enrolment for SSO users and building a general username/password form would create a
 * second, weaker credential surface for 400 people.
 *
 * There is exactly ONE local-password account: the break-glass administrator, which
 * exists so the system is recoverable when SSO itself is broken — which is precisely the
 * moment you cannot ask Entra ID anything. It carries TOTP, and every attempt against it
 * writes a HIGH severity audit event. It is therefore behind a disclosure, not presented
 * as a peer option: an employee who mistakes it for "the other way to sign in" will fail
 * and generate an audit alert.
 *
 * `GET /api/auth/methods` is public and says what the server supports; the UI follows it
 * rather than hardcoding, so disabling SSO in config changes this screen without a build.
 *
 * ── This screen is a STEP MACHINE, not a form with an optional MFA box ───────
 *
 * `POST /auth/login` rarely returns a session. It returns the NEXT STEP the account owes,
 * and each step's endpoint returns the step after that, until one of them finally answers
 * `AUTHENTICATED`. The loop below runs that sequence without knowing how long it is:
 *
 *     credentials ─▶ ┌──────────────────────────────────────────┐
 *                    │ outcome.status === 'AUTHENTICATED'?      │─ yes ─▶ navigate
 *                    │ else render outcome.nextStep, hold       │
 *                    │ outcome.stepToken, wait for its result   │─────┐
 *                    └──────────────────────────────────────────┘     │
 *                                    ▲                                │
 *                                    └────────────────────────────────┘
 *
 * On the break-glass account with no Entra tenant that loop is FOUR iterations —
 * MFA_ENROLMENT, PASSWORD_CHANGE, MFA_VERIFICATION, then a session — which is why nothing
 * here counts steps or assumes an order. See `loginSteps.ts` for the contract and for the
 * defect that made this rewrite necessary: the old page tested `response.mfaRequired`, a
 * key the API has never sent, so it fell through to `window.location.assign('/')`, got a
 * 401 from `/me`, and bounced back here. Correct credentials appeared to fail silently, and
 * with no SSO tenant configured there was no other way into the product at all.
 *
 * Step tokens live ten minutes. The countdown is on screen, and when it runs out the page
 * returns to the credentials form and SAYS SO, rather than leaving somebody typing codes
 * into a form whose token died four minutes ago.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

import { api } from '@frontend/api/client';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@frontend/components/ui/Button';
import { FieldShell, Input } from '@frontend/components/ui/Field';
import { Banner } from '@frontend/components/ui/Page';

import { describeAuthFailure } from './authErrors';
import { MfaEnrolment } from './MfaEnrolment';
import { MfaVerification } from './MfaVerification';
import { PasswordChange } from './PasswordChange';
import { readLoginOutcome, STEP_LABEL, unrecognisedOutcome, type LoginOutcome, type LoginStep } from './loginSteps';

interface AuthMethods {
  primaryMethod: string;
  microsoft365: { enabled: boolean; startUrl: string; label: string };
  localPassword: { enabled: boolean; label: string; note: string };
}

/** The half-finished sign-in currently on screen. `null` means "at the credentials form". */
interface PendingStep {
  step: LoginStep;
  stepToken: string;
  message: string;
  /** Epoch ms at which the step token stops being spendable. */
  expiresAt: number;
}

export function LoginPage(): JSX.Element {
  const [params] = useSearchParams();
  const location = useLocation();
  const [pending, setPending] = useState<PendingStep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  /** Set the instant a session exists, so an unmount-time timer cannot fire a "restart". */
  const signedIn = useRef(false);

  const methods = useQuery({
    queryKey: ['auth', 'methods'],
    queryFn: () => api.get<AuthMethods>('/auth/methods'),
    retry: 1,
    staleTime: 5 * 60_000,
  });

  // The SSO callback bounces back here with `?error=` when Entra ID refuses.
  const ssoError = params.get('error');
  const expired = params.get('reason') === 'expired';

  useEffect(() => {
    if (ssoError !== null) {
      setError(
        `Microsoft did not complete the sign-in (${ssoError}). If this keeps happening, your account may not be assigned to the AgentERP application in Entra ID — ask IT to check.`,
      );
    }
  }, [ssoError]);

  /**
   * Where to land. `ProtectedRoute` records the page the user was trying to reach in
   * `location.state.from`; anything else is refused so a crafted link cannot bounce the
   * browser off-site immediately after a successful sign-in.
   */
  function destination(): string {
    const from = (location.state as { from?: unknown } | null)?.from;
    if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) return from;
    return '/';
  }

  const restart = useCallback((reason: string): void => {
    setPending(null);
    setSecondsLeft(null);
    // No `setShowLocal(true)` any more. It existed to re-reveal the email form after a failed
    // step, because the form used to be hidden behind an "Administrator sign-in" toggle.
    // Mockup 8a shows it unconditionally, so there is nothing left to reveal.
    setError(reason);
  }, []);

  /**
   * THE LOOP. Every step endpoint returns one of these two things, so every step handler
   * hands its result here and this decides whether the sign-in is over.
   */
  const advance = useCallback((outcome: LoginOutcome): void => {
    if (outcome.status === 'AUTHENTICATED') {
      signedIn.current = true;
      setPending(null);
      setError(null);
      // A full navigation, not a client-side route change: the session cookies were set on
      // this response and `AuthProvider` must re-read the principal from `/auth/me`.
      window.location.assign(destination());
      return;
    }
    setError(null);
    setPending({
      step: outcome.nextStep,
      stepToken: outcome.stepToken,
      message: outcome.message,
      expiresAt: Date.now() + outcome.expiresIn * 1000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `destination` reads only location.state, which is stable for the life of this screen.
  }, []);

  // Step-token countdown. Expiry is a real event with a real consequence, so it is shown
  // and acted on rather than discovered by a confusing failure ten minutes later.
  useEffect(() => {
    if (pending === null) {
      setSecondsLeft(null);
      return;
    }
    function tick(): void {
      if (pending === null || signedIn.current) return;
      const remaining = Math.max(0, Math.round((pending.expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        restart(
          'This sign-in step expired before it was finished — they last ten minutes. Nothing was saved. ' +
            'Enter your email address and password again to start over.',
        );
      }
    }
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [pending, restart]);

  function startSso(): void {
    // A full navigation, not fetch: the OIDC flow is a browser redirect by design.
    window.location.assign(methods.data?.microsoft365.startUrl ?? '/api/auth/sso/microsoft');
  }

  async function submitPassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const body = await api.post<unknown>('/auth/login', {
        email: String(form.get('email') ?? ''),
        password: String(form.get('password') ?? ''),
      });
      // Never a cast. The whole defect was a cast to a shape the API does not produce.
      const outcome = readLoginOutcome(body);
      if (outcome === null) {
        setError(unrecognisedOutcome('POST /api/auth/login'));
        return;
      }
      advance(outcome);
    } catch (caught) {
      setError(
        describeAuthFailure(
          caught,
          'CREDENTIALS',
          'Sign-in failed and no reason was returned. Check your connection and try again.',
        ).message,
      );
    } finally {
      setBusy(false);
    }
  }

  const ssoEnabled = methods.data?.microsoft365.enabled ?? true;

  return (
    // Mockup 8a. Two panels: a form column and a 520px mesh panel carrying the mark at scale.
    // Below desktop the right panel is dropped entirely (8b: "same order, single column") — it
    // is atmosphere, and atmosphere is the first thing to go when the viewport is a phone.
    <div className="flex min-h-dvh bg-surface-base">
      <div className="flex flex-1 items-center justify-center px-gutter-phone py-10 tablet:px-gutter-tablet">
        <div className="w-full max-w-[376px]">
          <img src="/brand/logo/asb-logo.svg" alt="ASBInfotech" className="h-[34px] w-auto dark:hidden" />
          <img src="/brand/logo/asb-logo-dark.svg" alt="ASBInfotech" className="hidden h-[34px] w-auto dark:block" />

          {pending !== null ? (
            <div className="mt-[22px]">
              <StepHeader step={pending.step} secondsLeft={secondsLeft} />
              {error !== null && (
                <Banner tone="error" className="mb-4" data-testid="login-error">
                  {error}
                </Banner>
              )}
              {pending.step === 'MFA_ENROLMENT' && (
                <MfaEnrolment
                  key={pending.stepToken}
                  stepToken={pending.stepToken}
                  message={pending.message}
                  onAdvance={advance}
                  onRestart={restart}
                />
              )}
              {pending.step === 'MFA_VERIFICATION' && (
                <MfaVerification
                  key={pending.stepToken}
                  stepToken={pending.stepToken}
                  message={pending.message}
                  onAdvance={advance}
                  onRestart={restart}
                />
              )}
              {pending.step === 'PASSWORD_CHANGE' && (
                <PasswordChange
                  key={pending.stepToken}
                  stepToken={pending.stepToken}
                  message={pending.message}
                  onAdvance={advance}
                  onRestart={restart}
                />
              )}
              <button
                type="button"
                onClick={() =>
                  restart('Sign-in cancelled. Enter your email address and password to start again.')
                }
                className="mt-3 w-full text-body text-content-secondary hover:text-content"
                data-testid="cancel-sign-in"
              >
                Cancel and start over
              </button>
            </div>
          ) : (
            <>
              <h1 className="mt-[22px] text-title text-content">Sign in to asb people</h1>
              <p className="mt-2 text-body text-content-secondary">
                Leave, timesheets, payslips and approvals.
              </p>

              {expired && (
                <Banner tone="info" className="mt-4">
                  Your session ended. Sign in again to carry on — anything you had saved is still there.
                </Banner>
              )}
              {error !== null && (
                <Banner tone="error" className="mt-4" data-testid="login-error">
                  {error}
                </Banner>
              )}

              {/* SSO is the primary PATH — first in the order — but it is INK, not coral. The
                  single coral moment on this screen is the Sign in button below it. */}
              {ssoEnabled ? (
                <button
                  type="button"
                  onClick={startSso}
                  data-testid="sso-microsoft"
                  className="mt-[26px] flex h-12 w-full items-center justify-center gap-2.5 rounded-[13px] bg-content text-row font-bold text-content-inverse transition-token hover:opacity-90"
                >
                  <MicrosoftGlyph />
                  {methods.data?.microsoft365.label ?? 'Continue with company account'}
                </button>
              ) : (
                <Banner tone="warning" className="mt-[26px]" title="Company sign-in is unavailable">
                  Single sign-on is switched off on the server. Only the administrator account below can
                  sign in until it is restored.
                </Banner>
              )}

              <div className="my-[22px] flex items-center gap-3">
                <span className="h-px flex-1 bg-line" aria-hidden />
                <span className="text-eyebrow font-mono uppercase text-content-tertiary">or use email</span>
                <span className="h-px flex-1 bg-line" aria-hidden />
              </div>

              <form onSubmit={(event) => void submitPassword(event)} data-testid="breakglass-form">
                <div className="flex flex-col gap-2.5">
                  <FieldShell label="Work email" htmlFor="login-email" required fieldKey="email">
                    {/* rawName + autoComplete on: these credentials belong to the person typing. */}
                    <Input
                      id="login-email"
                      name="email"
                      rawName
                      type="email"
                      autoComplete="username"
                      placeholder="name@company.com"
                      required
                      data-testid="input-email"
                    />
                  </FieldShell>
                  <FieldShell label="Password" htmlFor="login-password" required fieldKey="password">
                    <Input
                      id="login-password"
                      name="password"
                      rawName
                      type="password"
                      autoComplete="current-password"
                      required
                      data-testid="input-password"
                    />
                  </FieldShell>
                </div>

                {/* THE coral moment on this screen. */}
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  size="lg"
                  className="mt-1.5"
                  isLoading={busy}
                  data-testid="submit-login"
                >
                  Sign in
                </Button>
              </form>

              {/* The break-glass warning sits WITH the form rather than behind a toggle. The
                  account it protects is the one an attacker would try first, and a warning
                  nobody sees until they have already chosen that path is decoration. */}
              <p className="mt-5 flex items-start gap-2 text-body text-content-tertiary">
                <ShieldAlert aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
                Email sign-in is for administrators and for client users without a company account.
                Every attempt is recorded as a high-severity audit event.
              </p>

              <p className="mt-[26px] border-t border-line-subtle pt-[18px] text-body text-content-tertiary">
                No access yet? Your account is created the first time you sign in with your company
                account. If you are still refused afterwards, ask HR to assign your role.
              </p>

              {methods.isError && (
                <p className="mt-4 flex items-start gap-1.5 text-body text-content-secondary">
                  <AlertTriangle aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
                  The server did not respond when asked which sign-in methods are available. Company
                  sign-in is shown as the default — if it fails, the server may be down.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* The mesh panel. Its own two-point gradient rather than the shared --canvas: the mockup
          pitches it stronger here (.22/.20 against the app canvas's .10/.12) because it is the
          whole right half of the screen rather than a wash behind content. */}
      <aside
        aria-hidden
        className="relative hidden w-[520px] flex-none flex-col justify-end overflow-hidden p-11 desktop:flex"
        style={{
          background:
            'radial-gradient(80% 70% at 78% 12%, rgba(249,69,94,.22) 0%, rgba(249,69,94,0) 62%),' +
            'radial-gradient(90% 80% at 10% 100%, rgba(56,104,190,.2) 0%, rgba(56,104,190,0) 58%),' +
            '#eef0f5',
        }}
      >
        {/* The mockup bleeds a 440px mark off the top-right corner. It assumes a SQUARE mark —
            ours is `asb-logo.svg`, a 3154x782 wordmark, and bleeding that slices the lettering,
            which reads as a broken image rather than as a watermark. The square favicon mark is
            the right asset for this treatment; at 14% opacity the upscale is invisible. */}
        <img
          src="/brand/favicon/Android.png"
          alt=""
          className="pointer-events-none absolute -right-[110px] -top-[70px] h-[440px] w-[440px] object-contain opacity-[0.14]"
        />
        <div className="relative">
          <p className="text-eyebrow font-mono uppercase tracking-[0.12em] text-brand-900">ASB Infotech</p>
          <p className="mt-3.5 max-w-[340px] text-title-sm text-content">
            One portal for people, time and pay.
          </p>
          <p className="mt-3 max-w-[340px] text-body text-content-secondary">
            Employees see their own leave and payslips. Client users see only the people assigned to
            their engagement.
          </p>
        </div>
      </aside>
    </div>
  );
}

/**
 * Names the step and counts the token down.
 *
 * The countdown turns red under a minute. A ten-minute budget is generous for typing a
 * code and tight for finding a phone, installing an authenticator and writing down eight
 * recovery codes — which is exactly what the first step asks of somebody.
 */
function StepHeader({ step, secondsLeft }: { step: LoginStep; secondsLeft: number | null }): JSX.Element {
  const minutes = secondsLeft === null ? 0 : Math.floor(secondsLeft / 60);
  const seconds = secondsLeft === null ? 0 : secondsLeft % 60;
  const urgent = secondsLeft !== null && secondsLeft <= 60;
  return (
    <div
      className="mb-4 flex items-baseline justify-between gap-2 border-b border-line pb-2"
      data-testid="step-header"
    >
      <p className="text-body uppercase tracking-[0.06em] text-content-secondary" data-testid="step-name">
        {STEP_LABEL[step]}
      </p>
      {secondsLeft !== null && (
        <p
          className={urgent ? 'tabular text-body font-medium text-error' : 'tabular text-body text-content-secondary'}
          data-testid="step-countdown"
        >
          {minutes}:{String(seconds).padStart(2, '0')} left
        </p>
      )}
    </div>
  );
}

function MicrosoftGlyph(): JSX.Element {
  return (
    <svg aria-hidden viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none">
      <rect x="1" y="1" width="8" height="8" fill="#F25022" />
      <rect x="11" y="1" width="8" height="8" fill="#7FBA00" />
      <rect x="1" y="11" width="8" height="8" fill="#00A4EF" />
      <rect x="11" y="11" width="8" height="8" fill="#FFB900" />
    </svg>
  );
}
